import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { Share2, Save, RefreshCw } from 'lucide-react';
import { API_URL } from './config';

// Admin UI for the "refer a friend" invite message templates — added 2026-09-11, mirroring
// BotGreetingsControl.js's structure/backend wiring (GET/POST /control-panel/settings/referral-messages,
// routes/control_panel.js). Consumed identically by xgo_Life App's Invite screen, xgo_web_app's
// ProfilePage referral share, and xgoBot's "refer"/"invite" free-text trigger (both roles).
const EMPTY_MESSAGES = { passenger: '', driver: '' };

const ReferralMessagesControl = () => {
  const [messages, setMessages] = useState(EMPTY_MESSAGES);
  const [tab, setTab] = useState('passenger');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/control-panel/settings/referral-messages`);
      const json = await res.json();
      if (json.success && json.data) {
        setMessages(json.data);
      } else {
        setAlert({ show: true, type: 'error', message: 'Failed to load referral messages.' });
      }
    } catch (err) {
      setAlert({ show: true, type: 'error', message: `Failed to load referral messages: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const updateMessage = (role, value) => {
    setMessages((prev) => ({ ...prev, [role]: value }));
  };

  const saveMessages = async () => {
    setSaving(true);
    setAlert({ show: false, type: '', message: '' });
    try {
      const res = await fetch(`${API_URL}/control-panel/settings/referral-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messages),
      });
      const json = await res.json();
      if (json.success) {
        setMessages(json.data);
        setAlert({ show: true, type: 'success', message: 'Referral messages saved.' });
      } else {
        setAlert({ show: true, type: 'error', message: json.message || 'Failed to save referral messages.' });
      }
    } catch (err) {
      setAlert({ show: true, type: 'error', message: `Failed to save referral messages: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Share2 size={22} />
        <Typography variant="h5" fontWeight={700}>
          Referral Messages
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The "refer a friend" invite message sent from xgoBot, the mobile app's Invite screen, and
        the web app's Profile page. Use <code>{'{referralCode}'}</code> anywhere in the text — it's
        replaced automatically with the sender's own referral code when sent.
      </Typography>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert({ ...alert, show: false })}>
          {alert.message}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="passenger" label="Passenger" />
        <Tab value="driver" label="Driver" />
      </Tabs>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {tab === 'passenger' ? 'Passenger message' : 'Driver message'}
          </Typography>
          <TextField
            multiline
            minRows={10}
            fullWidth
            size="small"
            value={messages[tab] ?? ''}
            onChange={(e) => updateMessage(tab, e.target.value)}
          />
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button variant="contained" startIcon={<Save size={16} />} onClick={saveMessages} disabled={saving || loading}>
          {saving ? 'Saving…' : 'Save Messages'}
        </Button>
        <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={fetchMessages} disabled={saving || loading}>
          Reload
        </Button>
      </Box>
    </Box>
  );
};

export default ReferralMessagesControl;
